import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type { JobFinancialSummary, JobCost, JobCredit } from '../types/financials';

export const useJobFinancialSummary = (jobId: string | undefined) => {
  return useQuery({
    queryKey: ['jobFinancialSummary', jobId],
    queryFn: async (): Promise<JobFinancialSummary> => {
      if (!jobId) throw new Error('Job ID is required');
      
      const response = await apiClient.get<JobFinancialSummary>(`/api/jobs/${jobId}/financial_summary/`);
      return response.data;
    },
    enabled: !!jobId,
  });
};

export const useJobCosts = (jobId: string | undefined) => {
  return useQuery({
    queryKey: ['jobCosts', jobId],
    queryFn: async (): Promise<JobCost[]> => {
      if (!jobId) throw new Error('Job ID is required');
      
      const response = await apiClient.get<JobCost[]>(`/api/jobs/${jobId}/costs/`);
      return response.data;
    },
    enabled: !!jobId,
  });
};

export const useAddJobCost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ jobId, costData }: { jobId: string; costData: Partial<JobCost> }) => {
      const response = await apiClient.post<JobCost>(`/api/jobs/${jobId}/add_cost/`, costData);
      return response.data;
    },
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ['jobCosts', jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobFinancialSummary', jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-workspace', String(jobId)] });
      queryClient.invalidateQueries({ queryKey: ['job-profit', String(jobId)] });
      queryClient.invalidateQueries({ queryKey: ['profit-desk'] });
      queryClient.invalidateQueries({ queryKey: ['cash-desk'] });
    },
  });
};

export const useDeleteJobCost = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ costId, jobId }: { costId: number; jobId: string }) => {
      await apiClient.delete(`/api/job-costs/${costId}/`);
    },
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ['jobCosts', jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobFinancialSummary', jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-workspace', String(jobId)] });
      queryClient.invalidateQueries({ queryKey: ['job-profit', String(jobId)] });
      queryClient.invalidateQueries({ queryKey: ['profit-desk'] });
      queryClient.invalidateQueries({ queryKey: ['cash-desk'] });
    },
  });
};

export const useJobCredits = (jobId: string | undefined) => {
  return useQuery({
    queryKey: ['jobCredits', jobId],
    queryFn: async (): Promise<JobCredit[]> => {
      if (!jobId) throw new Error('Job ID is required');
      
      const response = await apiClient.get<JobCredit[]>(`/api/jobs/${jobId}/credits/`);
      return response.data;
    },
    enabled: !!jobId,
  });
};

export const useAddJobCredit = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ jobId, creditData }: { jobId: string; creditData: Partial<JobCredit> }) => {
      const response = await apiClient.post<JobCredit>(`/api/jobs/${jobId}/add_credit/`, creditData);
      return response.data;
    },
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ['jobCredits', jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobFinancialSummary', jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-workspace', String(jobId)] });
      queryClient.invalidateQueries({ queryKey: ['job-profit', String(jobId)] });
      queryClient.invalidateQueries({ queryKey: ['profit-desk'] });
      queryClient.invalidateQueries({ queryKey: ['cash-desk'] });
    },
  });
};

export const useDeleteJobCredit = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ creditId, jobId }: { creditId: number; jobId: string }) => {
      await apiClient.delete(`/api/job-credits/${creditId}/`);
    },
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: ['jobCredits', jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobFinancialSummary', jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-workspace', String(jobId)] });
      queryClient.invalidateQueries({ queryKey: ['job-profit', String(jobId)] });
      queryClient.invalidateQueries({ queryKey: ['profit-desk'] });
      queryClient.invalidateQueries({ queryKey: ['cash-desk'] });
    },
  });
};